// Image URLs (poster, banner, logos, merchant logos, article images) to warm
// for a publisher experience payload.
import type { ExperiencePayload } from "@/types/experience";
import type { WarmConfig } from "@/modules/experiencePrefetchWarm";

function _assetUrl(
  protocol: string,
  host: string,
  filename: string | null | undefined,
): string | null {
  if (!filename || !host) {
    return null;
  }
  return `${protocol}//${host}/${filename}`;
}

function _merchantLogoUrl(
  protocol: string,
  sharedServer: string,
  filename: string,
): string | null {
  if (!filename || !sharedServer) {
    return null;
  }
  return `${protocol}//${sharedServer}/common/merchants/${filename}`;
}

function _pushFilename(
  set: Record<string, boolean>,
  protocol: string,
  host: string,
  filename: string,
) {
  const u = _assetUrl(protocol, host, filename);
  if (u) {
    set[u] = true;
  }
}

export function collectExperienceImageUrls(
  body: ExperiencePayload,
  cfg: Pick<WarmConfig, "protocol" | "assetServer" | "sharedServer">,
): string[] {
  const urls: Record<string, boolean> = {};
  const protocol = cfg.protocol;
  const assetServer = cfg.assetServer;
  const sharedServer = cfg.sharedServer;

  if (body.poster && body.poster.filename) {
    _pushFilename(urls, protocol, assetServer, body.poster.filename);
  }
  if (body.banner && body.banner.filename) {
    _pushFilename(urls, protocol, assetServer, body.banner.filename);
  }

  const logo = body.logo;
  if (logo && typeof logo === "object") {
    if (logo.mobile && logo.mobile.filename) {
      _pushFilename(urls, protocol, assetServer, logo.mobile.filename);
    }
    if (logo.desktop && logo.desktop.filename) {
      _pushFilename(urls, protocol, assetServer, logo.desktop.filename);
    }
    if (logo.all && logo.all.filename) {
      _pushFilename(urls, protocol, assetServer, logo.all.filename);
    }
  }

  const merchants = body.merchants;
  if (merchants && merchants.length) {
    for (let m = 0; m < merchants.length; m++) {
      const merch = merchants[m];
      if (merch && merch.logo && merch.logo.filename) {
        const ml = _merchantLogoUrl(
          protocol,
          sharedServer,
          merch.logo.filename,
        );
        if (ml) {
          urls[ml] = true;
        }
      }
    }
  }

  const articles = body.articles;
  const maxArticles = 36;
  if (articles && articles.length) {
    const cap = Math.min(articles.length, maxArticles);
    for (let i = 0; i < cap; i++) {
      const art = articles[i];
      if (!art || !art.images) {
        continue;
      }
      const keys = ["mobile", "desktop", "all"] as const;
      for (let k = 0; k < keys.length; k++) {
        const arr = art.images[keys[k]];
        if (!arr || !arr.length) {
          continue;
        }
        const img = arr[0];
        if (img && img.filename) {
          _pushFilename(urls, protocol, assetServer, img.filename);
        }
      }
    }
  }

  _pushFilename(
    urls,
    protocol,
    sharedServer,
    "common/pixmoto-logo-horiz-white.svg",
  );

  return Object.keys(urls);
}
